import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import apiClient from '../../services/api';
import useResource from '../../services/useResource';
import { AudioAsset, MaybePaginated } from '../../types/api';
import {
  baseName, childFolders, impliedFolders, parentOf,
} from '../AudioLibrary/folders';
import { formatTime } from '../AudioLibrary/formatTime';
import './AudioLibraryPicker.css';

export interface AudioSelection {
  assetId: number;
  start: number; // seconds — 0 or a saved marker's time
}

interface AudioLibraryPickerProps {
  selection: AudioSelection | null;
  onChange: (selection: AudioSelection | null) => void;
}

/**
 * Compact audio chooser for the wizard's upload step. Closed state is a
 * single field showing the chosen track (or "No music"); tapping it
 * opens a popover browser: ★ favorites pinned on top, then the folder
 * tree to drill in and out of. Stars toggle right in the popover and
 * sync with the Audio Library page. No selection → photo post;
 * a selection turns the post into a video.
 */
export default function AudioLibraryPicker({ selection, onChange }: AudioLibraryPickerProps) {
  const [open, setOpen] = useState(false);
  const [path, setPath] = useState('');
  const [markerLabel, setMarkerLabel] = useState('');
  const [savingMarker, setSavingMarker] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const previewRef = useRef<HTMLAudioElement | null>(null);

  const { data: assets, loading, setData } = useResource<AudioAsset[], MaybePaginated<AudioAsset>>(
    () => apiClient.get<MaybePaginated<AudioAsset>>('/audiolib/audio/'),
    [],
    { initial: [], transform: (raw) => (Array.isArray(raw) ? raw : raw.results || []) },
  );

  /* The browser expands INLINE (page scroll, no nested scrollbar), so
     outside-tap close would mis-fire on every page scroll touch —
     only Escape, the field toggle, and picking a track close it. */
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  if (loading || assets.length === 0) return null;

  const selected = selection
    ? assets.find((a) => a.id === selection.assetId) || null
    : null;
  const favorites = assets.filter((a) => a.is_favorite);
  const folders = childFolders(impliedFolders(assets.map((a) => a.folder)), path);
  const tracks = assets.filter((a) => a.folder === path);

  function pick(asset: AudioAsset): void {
    onChange({ assetId: asset.id, start: 0 });
    setOpen(false);
  }

  /* Save the preview's current playhead as a new start marker on the
     library track itself (same markers the Audio Library page manages),
     then jump the selection to it — no editor round-trip needed. */
  async function saveStartPoint(asset: AudioAsset): Promise<void> {
    const time = Math.round((previewRef.current?.currentTime ?? 0) * 1000) / 1000;
    const label = markerLabel.trim() || formatTime(time);
    setSavingMarker(true);
    try {
      const updated = await apiClient.patch<AudioAsset>(`/audiolib/audio/${asset.id}/`, {
        markers: [
          ...asset.markers,
          { id: `m_${Date.now().toString(36)}`, label, time },
        ],
      });
      setData((prev) => prev.map((a) => (a.id === updated.id ? updated : a)));
      setMarkerLabel('');
      onChange({ assetId: asset.id, start: time });
    } catch { /* picker is best-effort; the library page surfaces errors */ }
    setSavingMarker(false);
  }

  function toggleFavorite(asset: AudioAsset): void {
    const next = !asset.is_favorite;
    // Optimistic flip; revert if the PATCH fails.
    setData((prev) => prev.map((a) => (a.id === asset.id ? { ...a, is_favorite: next } : a)));
    apiClient.patch<AudioAsset>(`/audiolib/audio/${asset.id}/`, { is_favorite: next })
      .catch(() => {
        setData((prev) => prev.map((a) => (a.id === asset.id ? { ...a, is_favorite: !next } : a)));
      });
  }

  const trackRow = (a: AudioAsset): React.ReactElement => {
    const picked = selection?.assetId === a.id;
    return (
      <li key={a.id} className="wizard-audio-row">
        <button
          type="button"
          className={picked ? 'wizard-audio-row-pick picked' : 'wizard-audio-row-pick'}
          onClick={() => pick(a)}
          aria-pressed={picked}
        >
          <span className="wizard-audio-note" aria-hidden>♪</span>
          <span className="wizard-audio-name">{a.name}</span>
          <span className="muted wizard-audio-duration">
            {a.duration != null ? formatTime(a.duration) : '—'}
          </span>
          {picked && <span className="wizard-audio-check" aria-hidden>✓</span>}
        </button>
        <button
          type="button"
          className={a.is_favorite ? 'wizard-audio-fav is-fav' : 'wizard-audio-fav'}
          onClick={() => toggleFavorite(a)}
          aria-pressed={a.is_favorite}
          aria-label={a.is_favorite ? `Unfavorite ${a.name}` : `Favorite ${a.name}`}
        >
          {a.is_favorite ? '★' : '☆'}
        </button>
      </li>
    );
  };

  return (
    <section className="wizard-audio-picker" ref={rootRef}>
      <div className="section-label">Music (optional)</div>

      <div className="wizard-audio-field-row">
        <button
          type="button"
          className={selected ? 'wizard-audio-field has-track' : 'wizard-audio-field'}
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-haspopup="listbox"
        >
          <span className="wizard-audio-field-label">
            {selected ? `♪ ${selected.name}` : 'No music — photo post'}
          </span>
          <span className="wizard-audio-caret" aria-hidden>{open ? '▴' : '▾'}</span>
        </button>
        {selected && (
          <button
            type="button"
            className="ghost btn--icon-sm"
            onClick={() => onChange(null)}
            aria-label="Remove music"
            title="Remove music (back to photo post)"
          >
            ×
          </button>
        )}
      </div>

      {open && (
        <div className="wizard-audio-popover panel" role="listbox" aria-label="Audio library">
          {favorites.length > 0 && (
            <div className="wizard-audio-group">
              <div className="wizard-audio-group-label">★ Favorites</div>
              <ul className="wizard-audio-rows">{favorites.map(trackRow)}</ul>
            </div>
          )}

          <div className="wizard-audio-group">
            <div className="wizard-audio-group-label">
              {path ? (
                <button
                  type="button"
                  className="ghost btn--sm wizard-audio-back"
                  onClick={() => setPath(parentOf(path))}
                >
                  ← {baseName(path)}
                </button>
              ) : 'Library'}
            </div>
            <ul className="wizard-audio-rows">
              {folders.map((f) => (
                <li key={f} className="wizard-audio-row">
                  <button
                    type="button"
                    className="wizard-audio-row-pick wizard-audio-row-folder"
                    onClick={() => setPath(f)}
                  >
                    <span aria-hidden>📁</span>
                    <span className="wizard-audio-name">{baseName(f)}</span>
                  </button>
                </li>
              ))}
              {tracks.map(trackRow)}
              {folders.length === 0 && tracks.length === 0 && (
                <li className="muted wizard-audio-empty">Empty folder</li>
              )}
            </ul>
          </div>

          <div className="wizard-audio-popover-foot muted">
            Manage tracks and markers in the <Link to="/audio">Audio Library</Link>.
          </div>
        </div>
      )}

      {selected && (
        <>
        <div className="wizard-audio-start">
          <select
            aria-label="Start from"
            value={String(selection?.start ?? 0)}
            onChange={(e) => onChange({ assetId: selected.id, start: Number(e.target.value) })}
          >
            <option value="0">Beginning (0:00)</option>
            {selected.markers.map((m) => (
              <option key={m.id} value={String(m.time)}>
                {m.label} ({formatTime(m.time)})
              </option>
            ))}
            {/* The selected start may be a just-saved point not in the
              * markers list yet (or a marker deleted elsewhere). */}
            {selection && selection.start !== 0
              && !selected.markers.some((m) => m.time === selection.start) && (
              <option value={String(selection.start)}>{formatTime(selection.start)}</option>
            )}
          </select>
          {selected.file_url && (
            <audio
              ref={previewRef}
              key={`${selected.id}:${selection?.start ?? 0}`}
              src={`${selected.file_url}#t=${selection?.start ?? 0}`}
              controls
              preload="none"
              className="wizard-audio-preview"
            />
          )}
        </div>
        <div className="wizard-audio-mark">
          <input
            type="text"
            placeholder="New start point label (optional)"
            value={markerLabel}
            onChange={(e) => setMarkerLabel(e.target.value)}
            maxLength={100}
          />
          <button
            type="button"
            className="secondary btn--sm"
            onClick={() => saveStartPoint(selected)}
            disabled={savingMarker || !selected.file_url}
            title="Saves the preview's current position as a start marker on this track"
          >
            {savingMarker ? 'Saving…' : '+ Save start point'}
          </button>
        </div>
        </>
      )}
    </section>
  );
}
